import React from "react";
import { USE_LOCAL_FALLBACK } from "../../config/firebaseConfig";
import {
  uniforms_getBeginningInventory,
  uniforms_getDeliveries,
  uniforms_getInventory,
  uniforms_getSales
} from "../../lib/firestoreAdapters";
import { useOutletContext } from "react-router-dom";

function salesKey(campus) { return `uniforms_sales_${String(campus || "gen").toLowerCase()}`; }
function loadSales(campus) { try { const raw = localStorage.getItem(salesKey(campus)); return raw ? JSON.parse(raw) : []; } catch { return []; } }
function beginningKey(campus, year) { return `uniforms_beginning_inventory_${String(campus || "gen").toLowerCase()}_${year}`; }
function loadBeginning(campus, year) { try { const raw = localStorage.getItem(beginningKey(campus, year)); return raw ? JSON.parse(raw) : []; } catch { return []; } }

const MONTHS = [
  { value: "", label: "All Months" },
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" }
];

export default function SalesReport() {
  const { campus } = useOutletContext();
  const currentYear = new Date().getFullYear();
  const [sales, setSales] = React.useState([]);
  const [inv, setInv] = React.useState([]);
  const [deliveries, setDeliveries] = React.useState([]);
  const [beginningRows, setBeginningRows] = React.useState([]);
  const [reportYear, setReportYear] = React.useState(currentYear);
  const [monthFrom, setMonthFrom] = React.useState("");
  const [monthTo, setMonthTo] = React.useState("");

  React.useEffect(() => {
    (async () => {
      if (USE_LOCAL_FALLBACK) {
        setSales(loadSales(campus));
        setInv([]); // inventory lookup skipped in local; costs may be 0
        setDeliveries([]);
        setBeginningRows(loadBeginning(campus, reportYear));
      } else {
        const s = await uniforms_getSales(campus);
        const i = await uniforms_getInventory(campus);
        const d = await uniforms_getDeliveries(campus);
        const b = await uniforms_getBeginningInventory(campus, reportYear);
        setSales(s);
        setInv(i);
        setDeliveries(d);
        setBeginningRows(b);
      }
    })();
  }, [campus, reportYear]);

  function within(dateStr) {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return false;
    if (d.getFullYear() !== Number(reportYear)) return false;
    const month = d.getMonth() + 1;
    const from = monthFrom ? Number(monthFrom) : 1;
    const to = monthTo ? Number(monthTo) : 12;
    if (month < from || month > to) {
      return false;
    }
    return true;
  }

  const invCostMap = React.useMemo(() => {
    const m = new Map();
    (inv || []).forEach(r => m.set(`${String(r.product)}|${String(r.size)}`, Number(r.cost || 0)));
    return m;
  }, [inv]);

  const filtered = React.useMemo(() => {
    return (sales || []).filter(s => within(s.date));
  }, [sales, reportYear, monthFrom, monthTo]);
  const filteredDeliveries = React.useMemo(() => {
    return (deliveries || []).filter(d => within(d.date));
  }, [deliveries, reportYear, monthFrom, monthTo]);
  const beginningMap = React.useMemo(() => {
    const m = new Map();
    for (const row of beginningRows || []) {
      m.set(`${String(row.product)}|${String(row.size)}`, Number(row.beginningQty || 0));
    }
    return m;
  }, [beginningRows]);
  const yearOptions = React.useMemo(() => {
    const years = new Set([currentYear, currentYear - 1]);
    (sales || []).forEach(s => {
      const y = new Date(s.date).getFullYear();
      if (!Number.isNaN(y)) years.add(y);
    });
    (deliveries || []).forEach(d => {
      const y = new Date(d.date).getFullYear();
      if (!Number.isNaN(y)) years.add(y);
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [sales, deliveries, currentYear]);

  const ordersCount = filtered.length;
  const totals = filtered.reduce((acc, s) => {
    let costSum = 0;
    for (const it of (s.items || [])) {
      const key = `${String(it.product)}|${String(it.size)}`;
      const c = invCostMap.get(key) || 0;
      costSum += Number(c) * Number(it.qty || 0);
    }
    acc.revenue += Number(s.total || 0);
    acc.cost += costSum;
    acc.items += (s.items || []).reduce((x, it) => x + Number(it.qty || 0), 0);
    return acc;
  }, { revenue: 0, cost: 0, items: 0 });
  const deliveryTotals = React.useMemo(() => {
    return filteredDeliveries.reduce((acc, d) => {
      for (const it of (d.items || [])) {
        acc.items += Number(it.qty || 0);
        acc.cost += Number(it.qty || 0) * Number(it.cost || 0);
      }
      return acc;
    }, { items: 0, cost: 0 });
  }, [filteredDeliveries]);
  const beginningTotal = React.useMemo(() => {
    return (beginningRows || []).reduce((sum, r) => sum + Number(r.beginningQty || 0), 0);
  }, [beginningRows]);
  const estimatedEndingUnits = beginningTotal + deliveryTotals.items - totals.items;

  const grouped = React.useMemo(() => {
    const map = new Map();
    (beginningRows || []).forEach(b => {
      const key = `${String(b.product)}|${String(b.size)}`;
      map.set(key, {
        product: b.product,
        size: b.size,
        beginning: Number(b.beginningQty || 0),
        delivered: 0,
        qty: 0,
        revenue: 0,
        cost: 0
      });
    });
    filteredDeliveries.forEach(d => (d.items || []).forEach(it => {
      const key = `${String(it.product)}|${String(it.size)}`;
      const prev = map.get(key) || { product: it.product, size: it.size, beginning: beginningMap.get(key) || 0, delivered: 0, qty: 0, revenue: 0, cost: 0 };
      prev.delivered += Number(it.qty || 0);
      prev.cost += Number(it.qty || 0) * Number(it.cost || 0);
      map.set(key, prev);
    }));
    filtered.forEach(s => (s.items || []).forEach(it => {
      const key = `${String(it.product)}|${String(it.size)}`;
      const prev = map.get(key) || { product: it.product, size: it.size, beginning: beginningMap.get(key) || 0, delivered: 0, qty: 0, revenue: 0, cost: 0 };
      const c = invCostMap.get(key) || 0;
      prev.qty += Number(it.qty || 0);
      prev.revenue += Number(it.qty || 0) * Number(it.price || 0);
      if (!prev.delivered) prev.cost += Number(it.qty || 0) * Number(c);
      map.set(key, prev);
    }));
    return Array.from(map.values())
      .map(row => ({ ...row, ending: Number(row.beginning || 0) + Number(row.delivered || 0) - Number(row.qty || 0) }))
      .sort((a, b) => a.product.localeCompare(b.product) || String(a.size).localeCompare(String(b.size)));
  }, [filtered, filteredDeliveries, invCostMap, beginningRows, beginningMap]);

  function exportCSV() {
    const headers = ["Product","Size","Beginning","Delivered","Sold","Ending","Revenue","Cost","Margin"];
    const rows = grouped.map(g => [g.product, g.size, g.beginning || 0, g.delivered || 0, g.qty, g.ending || 0, g.revenue, g.cost, g.revenue - g.cost]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `uniforms_sales_${campus}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function printReport() {
    window.print();
  }

  return (
    <div>
      {USE_LOCAL_FALLBACK && (
        <div className="alert alert-warning">
          Development mode: using local browser storage for sales. In production this uses Firestore.
        </div>
      )}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Uniforms Sales Report — {campus.toUpperCase()}</h5>
        <div className="d-flex gap-2">
          <select className="form-select" value={reportYear} onChange={e => setReportYear(Number(e.target.value))} style={{ width: 120 }}>
            {yearOptions.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <select className="form-select" value={monthFrom} onChange={e => setMonthFrom(e.target.value)} style={{ width: 160 }}>
            {MONTHS.map((month) => (
              <option key={`from-${month.value || "all"}`} value={month.value}>{month.value ? `From ${month.label}` : month.label}</option>
            ))}
          </select>
          <select className="form-select" value={monthTo} onChange={e => setMonthTo(e.target.value)} style={{ width: 160 }}>
            {MONTHS.map((month) => (
              <option key={`to-${month.value || "all"}`} value={month.value}>{month.value ? `To ${month.label}` : month.label}</option>
            ))}
          </select>
          <button className="btn btn-outline-secondary" onClick={exportCSV}>Export CSV</button>
          <button className="btn btn-primary" onClick={printReport}>Print PDF</button>
        </div>
      </div>
      <div className="text-muted small mb-3">
        Showing {reportYear}{monthFrom || monthTo ? `, months ${monthFrom || 1} to ${monthTo || 12}` : " for the full year"}.
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-2"><div className="card summary-card slim secondary"><div className="card-body"><div className="summary-label">Beginning Units</div><div className="summary-value">{beginningTotal}</div></div></div></div>
        <div className="col-md-2"><div className="card summary-card slim info"><div className="card-body"><div className="summary-label">Delivered Units</div><div className="summary-value">{deliveryTotals.items}</div></div></div></div>
        <div className="col-md-2"><div className="card summary-card slim success"><div className="card-body"><div className="summary-label">Sold Units</div><div className="summary-value">{totals.items}</div></div></div></div>
        <div className="col-md-2"><div className="card summary-card slim primary"><div className="card-body"><div className="summary-label">Est. Ending Units</div><div className="summary-value">{estimatedEndingUnits}</div></div></div></div>
        <div className="col-md-2"><div className="card summary-card slim success"><div className="card-body"><div className="summary-label">Revenue</div><div className="summary-value">₱{totals.revenue.toLocaleString()}</div></div></div></div>
        <div className="col-md-2"><div className="card summary-card slim primary"><div className="card-body"><div className="summary-label">Orders</div><div className="summary-value">{ordersCount}</div></div></div></div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light"><tr><th>Product</th><th>Size</th><th className="text-end">Beginning</th><th className="text-end">Delivered</th><th className="text-end">Sold</th><th className="text-end">Ending</th><th className="text-end">Revenue</th><th className="text-end">Cost</th><th className="text-end">Margin</th></tr></thead>
              <tbody>
                {grouped.length === 0 ? (
                  <tr><td colSpan="9" className="text-center text-muted">No inventory movement in the selected year or month range</td></tr>
                ) : grouped.map((g, i) => (
                  <tr key={i}>
                    <td>{g.product}</td>
                    <td>{g.size}</td>
                    <td className="text-end">{g.beginning || 0}</td>
                    <td className="text-end">{g.delivered || 0}</td>
                    <td className="text-end">{g.qty}</td>
                    <td className="text-end">{g.ending || 0}</td>
                    <td className="text-end">₱{Number(g.revenue).toLocaleString()}</td>
                    <td className="text-end">₱{Number(g.cost).toLocaleString()}</td>
                    <td className="text-end">₱{Number(g.revenue - g.cost).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card mt-3">
        <div className="card-body">
          <h6 className="fw-bold mb-2">Sales Receipts ({filtered.length})</h6>
          <div className="table-responsive">
            <table className="table table-sm">
              <thead className="table-light"><tr><th>Date</th><th className="text-end">Items</th><th className="text-end">Total</th><th>Details</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan="4" className="text-center text-muted">No receipts</td></tr>
                ) : filtered.map((r, i) => (
                  <tr key={i}>
                    <td>{r.date}</td>
                    <td className="text-end">{(r.items || []).reduce((n,it)=>n+Number(it.qty||0),0)}</td>
                    <td className="text-end">₱{Number(r.total || 0).toLocaleString()}</td>
                    <td>
                      {(r.items || []).map((it, idx) => (
                        <div key={idx} className="small text-muted">
                          {it.product} • {it.size} × {Number(it.qty || 0)} @ ₱{Number(it.price || 0).toLocaleString()}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          .btn, .form-select { display: none !important; }
        }
      `}</style>
    </div>
  );
}
